import { Link } from "react-router-dom";
import Layout from "@/components/site/Layout";
import Breadcrumb from "@/components/site/Breadcrumb";
import ContactForm from "@/components/site/ContactForm";
import { newsCategories } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";

const values = [
  { icon: "ri-speed-line", title: "快而不噪", text: "第一时间推送要闻，同时也求快更求准，避免在慌乱中失真。" },
  { icon: "ri-stack-line", title: "广而有序", text: "娱乐、社会、科技、财经、体育分门别类，信息再多也有条理。" },
  { icon: "ri-shield-check-line", title: "信源可溯", text: "每条快讯标注来源与进展，存疑信息绝不当作事实传播。" },
  { icon: "ri-cup-line", title: "陪你吃瓜", text: "用平实好读的表达，陪你看遍一天大事与人间烟火。" },
];

const principles = [
  "信源可溯、编辑核实后再发布",
  "事实与观点明确分开呈现",
  "仍在演进的消息标注进行状态",
  "标题克制，不耸动、不标题党",
];

const quotes = [
  { name: "林先生 · 通勤上班族", text: "早班地铁上花十分钟翻完早报，一天的重点基本就齐了，真的省心。" },
  { name: "阿禾 · 大学生", text: "栏目分得很清楚，晚上躺着刷晚报也不会觉得信息爆炸，很舒服。" },
  { name: "老周 · 退休教师", text: "快讯后面都会标来源，写得克制不夸大，这点我信得过。" },
];

function AboutPage() {
  usePageMeta({
    title: "关于我们 - 吃瓜新闻：定位、理念与内容原则",
    description:
      "了解吃瓜新闻：一个全天滚动更新的每日快讯频道。我们以信息可溯、编辑核实为原则，用快讯、直播与早报晚报帮你在最短时间里看遍一天大事。",
    canonical: "https://youbijing.com.cn/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://youbijing.com.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://youbijing.com.cn/about" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于吃瓜新闻",
        url: "https://youbijing.com.cn/about",
        inLanguage: "zh-CN",
        description: "吃瓜新闻是一个全天滚动更新的每日快讯频道，坚持信源可溯与编辑核实。",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "吃瓜新闻",
        url: "https://youbijing.com.cn/",
        email: "contact@chigua-news.example",
        description: "全天滚动更新的每日快讯频道",
      },
    ],
  });

  return (
    <Layout>
      {/* 头图 */}
      <div className="border-b border-background-200">
        <div className="mx-auto max-w-6xl px-4 py-7 md:px-6 md:py-9">
          <Breadcrumb crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-5 font-heading text-3xl font-black text-foreground-950 md:text-5xl">关于吃瓜新闻</h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-7 text-foreground-600">
            我们是一个全天滚动更新的每日快讯频道，把今日要闻、即时速递、早报晚报与直播内容整理在一起，
            希望你在最短的时间里，看遍一天大事，不错过任何重要信息。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-14">
        {/* 品牌定位 */}
        <section className="grid items-center gap-8 lg:grid-cols-2" aria-label="品牌定位">
          <div>
            <span className="text-sm font-medium text-primary-600">我们是谁</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">你的每日新闻早餐</h2>
            <div className="mt-5 space-y-4 text-[15px] leading-7 text-foreground-700">
              <p>
                这个时代不缺信息，缺的是整理。吃瓜新闻把散落在各处的要闻，按栏目归类、按时间滚动，
                再用早报与晚报把一天重点收拢起来，让'补信息'这件事变得轻快又从容。
              </p>
              <p>
                无论是通勤路上的碎片时间，还是睡前的放松一刻，你都能在这里快速找到值得关注的几件事，
                然后在轻松之外，多一份看得懂、辨得清的踏实。
              </p>
            </div>
          </div>
          <figure className="overflow-hidden rounded-2xl">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20flat%20illustration%20of%20a%20morning%20newspaper%20reading%20scene%20with%20coffee%2C%20glasses%20and%20folded%20newspaper%20on%20a%20desk%2C%20editorial%20minimalist%20style%2C%20cream%20and%20coral%20red%20palette%2C%20soft%20warm%20light%2C%20cozy%20storytelling%20mood&width=900&height=700&seq=about-office-14&orientation=portrait"
              alt="编辑团队清晨阅读整理每日要闻的插画"
              title="吃瓜新闻编辑部清晨工作插画"
              className="aspect-[4/3] w-full object-cover"
            />
          </figure>
        </section>

        {/* 理念 */}
        <section className="mt-16" aria-label="发展理念">
          <div className="mb-7 text-center">
            <span className="text-sm font-medium text-accent-700">我们坚持什么</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">我们的理念</h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v) => (
              <div key={v.title} className="rounded-xl border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-2xl text-primary-600">
                  <i className={v.icon} />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">{v.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{v.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 内容原则 + 口碑 */}
        <section className="mt-16 grid gap-8 lg:grid-cols-2" aria-label="内容原则与口碑">
          <div className="rounded-2xl bg-background-100 p-6 md:p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-100 text-2xl text-accent-700">
              <i className="ri-list-check-2" />
            </span>
            <h2 className="mt-4 font-heading text-2xl font-black text-foreground-950">内容原则</h2>
            <ul className="mt-5 space-y-3">
              {principles.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-[15px] leading-6 text-foreground-700">
                  <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <span className="text-sm font-medium text-secondary-700">读者怎么说</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">来自读者的口碑</h2>
            <div className="mt-6 space-y-4">
              {quotes.map((q) => (
                <blockquote key={q.name} className="rounded-xl border border-background-200 bg-background-50 p-5">
                  <p className="text-[15px] leading-7 text-foreground-700">“{q.text}”</p>
                  <footer className="mt-3 text-sm font-medium text-foreground-500">— {q.name}</footer>
                </blockquote>
              ))}
            </div>
          </div>
        </section>

        {/* 频道地图 */}
        <section className="mt-16 rounded-2xl bg-primary-600 px-6 py-8 text-background-50 md:px-10" aria-label="频道地图">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="font-heading text-2xl font-black md:text-3xl">从这里开始逛</h2>
              <p className="mt-2 text-sm text-background-50/80">今日要闻、直播滚动与五大栏目，总有一款适合你。</p>
            </div>
            <div className="flex flex-wrap gap-2.5">
              <Link
                to="/live"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-background-50 px-4 py-2.5 text-sm font-medium text-primary-700 hover:bg-accent-100"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500 animate-pulse-dot" /> 看直播
              </Link>
              {newsCategories.slice(0, 5).map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="whitespace-nowrap rounded-md bg-background-50/15 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-background-50/25"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* 联系我们 */}
        <section className="mt-16" id="contact" aria-label="联系我们">
          <div className="mb-7 text-center">
            <span className="text-sm font-medium text-primary-600">保持联系</span>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">联系我们</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-foreground-600">
              有新闻线索、内容纠错或栏目建议？欢迎通过下方表单留言，或直接来信。
            </p>
          </div>
          <div className="grid gap-8 lg:grid-cols-5">
            <div className="space-y-4 lg:col-span-2">
              <div className="flex items-start gap-3 rounded-xl border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-mail-line" />
                </span>
                <div>
                  <h3 className="font-semibold text-foreground-900">邮件联系</h3>
                  <p className="mt-1 text-sm text-foreground-600">contact@chigua-news.example</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-time-line" />
                </span>
                <div>
                  <h3 className="font-semibold text-foreground-900">在线时间</h3>
                  <p className="mt-1 text-sm text-foreground-600">工作日 08:30 - 22:00，快讯全天滚动</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-chat-3-line" />
                </span>
                <div>
                  <h3 className="font-semibold text-foreground-900">选题建议</h3>
                  <p className="mt-1 text-sm text-foreground-600">也可前往直播与栏目页，第一时间获取更新</p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-background-200 bg-background-100 p-6 md:p-7 lg:col-span-3">
              <h3 className="font-heading text-lg font-bold text-foreground-900">写信给我们</h3>
              <div className="mt-5">
                <ContactForm />
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

export default AboutPage;